import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { useTimeout } from '@/js/util';

const THREE_MINUTES = 1000 * 60 * 3;
const POLL_DELAY = 1000 / 3;

export default class extends Controller {
  static targets = ['loader', 'error'];

  static values = {
    pollPaths: Array,
    retryPaths: Array,
  };

  initialize() {
    this.failedIndexes = new Set();
  }

  connect() {
    useTimeout(this);
    this.#poll(this.pollPathsValue.map((_path, index) => index));
  }

  retry() {
    const indexes = [...this.failedIndexes];
    if (!indexes.length) {
      return;
    }

    this.failedIndexes.clear();
    this.#showLoader();

    Promise.all(indexes.map((index) => this.#requestRetry(index))).finally(() =>
      this.#poll(indexes)
    );
  }

  submitForm() {
    this.element.requestSubmit();
  }

  #poll(indexes) {
    this.stopped = false;

    const pending = new Set(indexes);
    const pollPromises = indexes.map((index) =>
      this.#pollFor(this.pollPathsValue[index])
        .then(() => pending.delete(index))
        .catch(() => {
          pending.delete(index);
          this.failedIndexes.add(index);
        })
    );

    const deadline = new Promise((resolve) => this.timeout(THREE_MINUTES, resolve));

    Promise.race([Promise.all(pollPromises), deadline]).then(() => {
      this.stopped = true;
      // Anything still pending at this point only happens on a deadline timeout; treat it as
      // failed so it is offered for retry rather than left in limbo.
      pending.forEach((index) => this.failedIndexes.add(index));

      if (this.failedIndexes.size) {
        this.#showError();
      } else {
        this.submitForm();
      }
    });
  }

  #showLoader() {
    this.loaderTarget.classList.remove('hidden');
    this.errorTarget.classList.add('hidden');
  }

  #showError() {
    this.loaderTarget.classList.add('hidden');
    this.errorTarget.classList.remove('hidden');
  }

  #requestRetry(index) {
    return new FetchRequest('POST', this.retryPathsValue[index])
      .perform()
      .catch((error) => console.error(error));
  }

  #pollFor(uri) {
    return new Promise((resolve, reject) => this.#pollImage(uri, resolve, reject));
  }

  #pollImage(uri, resolve, reject, delay = 0) {
    this.timeout(delay, () => {
      // The batch may have already timed out or failed on another path; stop polling this URI
      // rather than sending another request that nothing is waiting on anymore.
      if (this.stopped) {
        return reject(new Error('export polling timed out'));
      }

      new FetchRequest('GET', uri, { redirect: 'error' })
        .perform()
        .then((response) => {
          if (this.stopped) {
            return reject(new Error('export polling timed out'));
          }

          const { statusCode } = response;

          switch (statusCode) {
            case 200:
              return resolve(response);
            case 202:
              return this.#pollImage(uri, resolve, reject, POLL_DELAY);
            case 500:
              throw new Error('image generation failed');
            default:
              throw new Error(`unknown HTTP status ${statusCode}, halting execution`);
          }
        })
        .catch(reject);
    });
  }
}
